// PWA-Kaltstart-Fix: iOS-Standalone initialisiert den Layout-Viewport
// (100dvh) beim Launch gelegentlich stale (Splash/Rotation/persistierter
// Tastatur-Shrink) und aktualisiert ihn erst nach der ersten Wischgeste —
// die Bottom-Bar „hängt in der Mitte". Die Shell hängt deshalb an einer
// JS-gepflegten CSS-Var `--app-height` (Fallback 100dvh).
//
// Zweiter Kaltstart-Fall (schwarzer Balken unterm Footer): auch
// visualViewport.height kann beim Standalone-Launch zu KLEIN sein und iOS
// feuert danach nicht zwingend ein resize. Deshalb (a) zählt das MAXIMUM
// aus Layout- und Visual-Höhe (ohne Tastatur sind beide gleich — der stale
// Wert ist immer der kleinere), (b) synchen wir zusätzlich auf
// visualViewport-scroll/visibilitychange/touchend und (c) laufen nach
// Boot/pageshow verzögerte Re-Syncs, weil iOS die Geometrie erst Frames
// später korrigiert.
//
// Dritter Fall (Gerätetest iPhone 2026-07-08): iOS kann BEIDE Quellen
// stale klein melden (~ Screen minus Safari-Bottom-Bar, die es im
// Standalone gar nicht gibt) — max() aus zwei falschen Werten bleibt
// falsch. Im iPhone-STANDALONE ist die App aber garantiert Vollbild
// (kein Split View auf iPhones) → dort ist die orientierungsbewusste
// screen-Dimension die verlässliche Untergrenze (screenFloor). Bewusst
// NUR iPhone + Standalone: iPad-Split-View und Browser-Tabs haben
// legitim kleinere Fenster als der Screen.

import { isFramed } from './app-embedding'

export interface AppHeightInput {
  /** visualViewport.scale (1 = kein Pinch-Zoom). */
  scale: number
  /** window.innerHeight (Layout-Viewport). */
  layoutH: number
  /** visualViewport.height (sichtbarer Viewport), gerundet. */
  visualH: number
  /** Ist gerade ein Eingabefeld fokussiert (Tastatur plausibel offen)? */
  fieldFocused: boolean
  /** Garantierte Mindesthöhe (iPhone-Standalone: screen-Dimension), sonst null. */
  screenFloor?: number | null
}

/** Toleranz, ab der visual < layout als „iOS-Tastatur offen" gilt. */
export const KEYBOARD_DELTA_PX = 24

/**
 * Liefert die Ziel-Höhe für --app-height oder null (= Sync aussetzen).
 * Pure Logik (getestet):
 * - Pinch-Zoom (scale ≠ 1) nie einfrieren → null.
 * - iOS-Tastaturfall (Feld fokussiert + visual deutlich < layout) → null —
 *   dort schrumpft NUR der Visual-Viewport; die Bar soll wie bisher am
 *   Layout-Boden bleiben (Sheets sind eigenständig visualViewport-verankert).
 *   Android (resizes-content) schrumpft layoutH mit → Guard greift nicht.
 * - Sonst max(layoutH, visualH, screenFloor): ohne Tastatur sind Layout und
 *   Visual identisch; weicht einer ab, ist es der stale Kaltstart-Wert (zu
 *   klein → schwarzer Balken bzw. mittige Bar). screenFloor deckt den Fall
 *   ab, dass BEIDE stale sind (iPhone-Standalone = garantiert Vollbild).
 */
export function computeAppHeightTarget({
  scale,
  layoutH,
  visualH,
  fieldFocused,
  screenFloor = null,
}: AppHeightInput): number | null {
  if (scale !== 1) return null
  if (fieldFocused && visualH < layoutH - KEYBOARD_DELTA_PX) return null
  return Math.max(layoutH, visualH, screenFloor ?? 0)
}

/** iPhone-Standalone erkannt? Nur dort ist Vollbild garantiert. */
function isIphoneStandalone(): boolean {
  if (!/iPhone/.test(navigator.userAgent)) return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches
}

/** Orientierungsbewusste Screen-Höhe (pure, getestet): iOS lässt
 *  screen.width/height bei Rotation fix (Portrait-Werte) — Portrait nimmt
 *  die lange, Landscape die kurze Seite, egal wie herum sie gemeldet sind. */
export function pickScreenHeight(portrait: boolean, screenW: number, screenH: number): number {
  return portrait ? Math.max(screenH, screenW) : Math.min(screenH, screenW)
}

function screenViewportHeight(): number {
  return pickScreenHeight(
    window.matchMedia('(orientation: portrait)').matches,
    window.screen.width,
    window.screen.height,
  )
}

function isEditableFocused(): boolean {
  const el = document.activeElement
  return !!el && el.matches('input, textarea, select, [contenteditable="true"]')
}

function syncAppHeight() {
  const vv = window.visualViewport
  const layoutH = window.innerHeight
  // A frame owns only its layout viewport; an iOS visualViewport can report
  // the outer screen during launch/resume. Never let that enlarge the iframe.
  const visualH = isFramed()
    ? Math.min(Math.round(vv?.height ?? layoutH), layoutH)
    : Math.round(vv?.height ?? layoutH)
  const target = computeAppHeightTarget({
    scale: vv?.scale ?? 1,
    layoutH,
    visualH,
    fieldFocused: isEditableFocused(),
    screenFloor: !isFramed() && isIphoneStandalone() ? screenViewportHeight() : null,
  })
  if (target === null) return
  document.documentElement.style.setProperty('--app-height', `${target}px`)
  // iOS scrollt bei Input-Fokus das DOKUMENT (auch ohne Overflow) und lässt
  // es nach Tastatur-Schließen teils versetzt stehen — die Shell hinge dann
  // sichtbar verschoben. Ohne fokussiertes Feld gehört scrollY immer auf 0.
  // Nur in der Shell (overflow-hidden-Root): Auth/Welcome scrollen das
  // Dokument legitim — dort existiert #shell-root nicht.
  if (!isEditableFocused() && window.scrollY !== 0 && document.querySelector('[data-shell-root]')) {
    window.scrollTo(0, 0)
  }
}

/** iOS korrigiert die Launch-Geometrie oft erst Frames/Sekunden später —
 *  ohne dass zwingend ein resize feuert. Gestaffelt nachmessen. */
const RESYNC_DELAYS_MS = [250, 1000, 3000]

function scheduleResyncs() {
  for (const delay of RESYNC_DELAYS_MS) {
    window.setTimeout(syncAppHeight, delay)
  }
}

/** Einmal beim Boot aufrufen (Modul-Scope in main.tsx, wie bootTheme). */
export function bootAppHeight() {
  if (typeof window === 'undefined') return
  if (heightBooted) return
  heightBooted = true
  syncAppHeight()
  scheduleResyncs()
  window.visualViewport?.addEventListener('resize', syncAppHeight)
  // iOS feuert nach Geometrie-Korrekturen teils nur visualViewport-scroll
  // (z. B. bei der ersten Wischgeste) — auch das als Sync-Anlass nutzen.
  window.visualViewport?.addEventListener('scroll', syncAppHeight)
  window.addEventListener('resize', syncAppHeight)
  window.addEventListener('orientationchange', syncAppHeight)
  // Die erste Berührung zwingt iOS zur Geometrie-Korrektur (der „Wisch
  // repariert es"-Effekt) — direkt danach nachmessen, idempotent + billig.
  window.addEventListener('touchend', syncAppHeight, { passive: true })
  // pageshow deckt den bfcache-/Standalone-Relaunch ab (Kaltstart-Fall),
  // visibilitychange den App-Switcher-Resume.
  window.addEventListener('pageshow', () => {
    syncAppHeight()
    scheduleResyncs()
  })
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      syncAppHeight()
      scheduleResyncs()
    }
  })
}

let heightBooted = false
